package com.evoapps.evoshift.data.mapper

import com.evoapps.evoshift.data.dto.hiring.JobApplicationDto
import com.evoapps.evoshift.data.dto.hiring.JobPostCvDto
import com.evoapps.evoshift.data.dto.hiring.RejectionMailDto
import com.evoapps.evoshift.data.dto.hiring.JobPostDto
import com.evoapps.evoshift.data.dto.hiring.JobPostWriteDto
import com.evoapps.evoshift.domain.model.ApplicantCv
import com.evoapps.evoshift.domain.model.ApplicantStatus
import com.evoapps.evoshift.domain.model.CvFileType
import com.evoapps.evoshift.domain.model.CvRequirement
import com.evoapps.evoshift.domain.model.CvSettings
import com.evoapps.evoshift.domain.model.RejectionMailTemplate
import com.evoapps.evoshift.domain.model.JobApplication
import com.evoapps.evoshift.domain.model.JobContractType
import com.evoapps.evoshift.domain.model.JobPost
import com.evoapps.evoshift.domain.model.JobPostStatus
import com.evoapps.evoshift.domain.model.PayUnit

/** SH-125 job posts and applicants between Firestore and the domain. Unknown values read as defaults. */
object HiringMapper {
    fun toDomain(dto: JobPostDto, id: String): JobPost = JobPost(
        id = id,
        workplaceId = dto.workplaceId,
        status = JobPostStatus.entries.firstOrNull { it.name == dto.status } ?: JobPostStatus.DRAFT,
        title = dto.title,
        role = dto.role,
        description = dto.description,
        hoursText = dto.hoursText,
        contractType = JobContractType.entries.firstOrNull { it.name == dto.contractType } ?: JobContractType.OTHER,
        payMin = dto.payMin,
        payMax = dto.payMax,
        payUnit = PayUnit.entries.firstOrNull { it.name == dto.payUnit } ?: PayUnit.HOUR,
        currency = dto.currency,
        city = dto.city,
        closesOn = dto.closesOn,
        employerName = dto.employerName,
        privacyContact = dto.privacyContact,
        lang = dto.lang,
        createdAt = dto.createdAt,
        createdBy = dto.createdBy,
        updatedAt = dto.updatedAt,
        updatedBy = dto.updatedBy,
        totalApplicants = dto.counts?.total ?: 0,
        newApplicants = dto.counts?.new ?: 0,
        closedAt = dto.closedAt,
        cv = dto.cv?.let(::toDomain) ?: CvSettings(),
        recruiterIds = dto.recruiterIds.filter { it.isNotBlank() }.distinct(),
        rejectionMail = dto.rejectionMail?.let { RejectionMailTemplate(it.enabled, it.subject, it.body) } ?: RejectionMailTemplate()
    )

    /** SH-210: unknown kinds are dropped, odd sizes kept within 1..5 MB (as the server reads them). */
    fun toDomain(dto: JobPostCvDto): CvSettings = CvSettings(
        requirement = CvRequirement.entries.firstOrNull { it.name == dto.requirement } ?: CvRequirement.OPTIONAL,
        types = dto.types.mapNotNull(CvFileType::fromId).toSet().ifEmpty { CvSettings().types },
        maxMb = dto.maxMb.coerceIn(CvSettings.MIN_MB, CvSettings.MAX_MB)
    )

    fun toDto(cv: CvSettings): JobPostCvDto = JobPostCvDto(
        requirement = cv.requirement.name,
        // In a fixed order, so an unchanged post writes the same list.
        types = CvFileType.entries.filter { it in cv.types }.map { it.id },
        maxMb = cv.maxMb
    )

    fun toWriteDto(post: JobPost, id: String): JobPostWriteDto = JobPostWriteDto(
        id = id,
        workplaceId = post.workplaceId,
        status = post.status.name,
        title = post.title,
        role = post.role,
        description = post.description,
        hoursText = post.hoursText,
        contractType = post.contractType.name,
        payMin = post.payMin,
        payMax = post.payMax,
        payUnit = post.payUnit.name,
        currency = post.currency,
        city = post.city,
        closesOn = post.closesOn,
        employerName = post.employerName,
        privacyContact = post.privacyContact,
        lang = post.lang,
        createdAt = post.createdAt,
        createdBy = post.createdBy,
        updatedAt = post.updatedAt,
        updatedBy = post.updatedBy,
        cv = toDto(post.cv),
        recruiterIds = post.recruiterIds,
        rejectionMail = RejectionMailDto(post.rejectionMail.enabled, post.rejectionMail.subject, post.rejectionMail.body)
    )

    fun toDomain(dto: JobApplicationDto, id: String, postId: String): JobApplication = JobApplication(
        id = id,
        postId = dto.postId.ifEmpty { postId },
        workplaceId = dto.workplaceId,
        name = dto.name,
        email = dto.email?.takeIf { it.isNotBlank() },
        phone = dto.phone?.takeIf { it.isNotBlank() },
        availability = dto.availability?.takeIf { it.isNotBlank() },
        note = dto.note?.takeIf { it.isNotBlank() },
        futureRecruitment = dto.consents?.futureRecruitment == true,
        cv = dto.cv?.takeIf { it.path.isNotEmpty() }?.let { ApplicantCv(it.fileName, it.contentType, it.sizeBytes) },
        status = ApplicantStatus.fromId(dto.status),
        managerNote = dto.managerNote?.takeIf { it.isNotBlank() },
        decidedAt = dto.decidedAt,
        createdAt = dto.createdAt,
        lang = dto.lang.takeIf { it == "en" || it == "uk" } ?: "pl",
        readBy = dto.readBy.toSet(),
        starredBy = dto.starredBy.toSet(),
        rejectionMailAt = dto.rejectionMailQueuedAt
    )
}
